import type {ResizeCallback} from 're-resizable'
import {Resizable} from 're-resizable'
import {memo, useCallback, useMemo, useRef} from 'react'
import type * as React from 'react'
import {Transition} from 'react-transition-group'

import styles from './Sidebar.css'

const RESIZABLE_ENABLE_CONFIG = {
  top: false,
  right: false,
  bottom: false,
  left: true,
  topRight: false,
  bottomRight: false,
  bottomLeft: false,
  topLeft: false,
}

const TRANSITION_DURATION = 250

type Props = {
  children: React.ReactNode
  sidebarWidth: number
  minWidth: number
  maxWidth: number
  isOpen?: boolean
  onResize: (deltaWidth: number) => void
}

function Sidebar({children, onResize, sidebarWidth, maxWidth, minWidth, isOpen = true}: Props) {
  const nodeRef = useRef(null)
  const onResizeStop: ResizeCallback = useCallback(
    (e, direction, ref, delta) => {
      onResize(delta.width)
    },
    [onResize],
  )

  const size = useMemo(
    () => ({
      width: sidebarWidth,
      height: '100%',
    }),
    [sidebarWidth],
  )

  return (
    <Transition
      nodeRef={nodeRef}
      in={isOpen}
      timeout={TRANSITION_DURATION}
      exit={false}
      mountOnEnter
      unmountOnExit
    >
      {state => (
        <aside
          ref={nodeRef}
          className={styles.sidebar}
          style={{
            right: state === 'exiting' || state === 'exited' ? sidebarWidth * -1 : 0,
          }}
        >
          <Resizable
            size={size}
            enable={RESIZABLE_ENABLE_CONFIG}
            minWidth={minWidth}
            maxWidth={maxWidth}
            onResizeStop={onResizeStop}
          >
            {children}
          </Resizable>
        </aside>
      )}
    </Transition>
  )
}

export default memo(Sidebar)
